import { Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ToolDataLimitsApi } from '@tfs/tool-library-data-api';

type QuotaLevel = 'success' | 'warning' | 'error';

@Component({
  selector: 'tfs-assembly-quota-indicator',
  imports: [MatProgressBarModule, MatTooltipModule],
  templateUrl: './assembly-quota-indicator.component.html',
  styleUrl: './assembly-quota-indicator.component.scss',
})
export class AssemblyQuotaIndicatorComponent {
  private readonly limitsApi = inject(ToolDataLimitsApi);

  public readonly refreshTrigger = input<number>(0);

  private readonly limitsResource = rxResource({
    params: () => this.refreshTrigger(),
    stream: () => this.limitsApi.getToolAssemblyLimits(),
  });

  protected readonly limits = this.limitsResource.value;
  protected readonly isLoading = this.limitsResource.isLoading;

  protected readonly percentage = computed(() => {
    const limits = this.limits();
    if (!limits || limits.maximumToolAssemblies === 0) return 0;
    return Math.min(
      Math.round((limits.currentToolAssemblies / limits.maximumToolAssemblies) * 100),
      100
    );
  });

  protected readonly isMaxed = computed(() => {
    const limits = this.limits();
    if (!limits) return false;
    return limits.currentToolAssemblies >= limits.maximumToolAssemblies;
  });

  protected readonly level = computed((): QuotaLevel => {
    const pct = this.percentage();
    if (pct <= 50) return 'success';
    if (pct <= 85) return 'warning';
    return 'error';
  });

  protected readonly tooltip = computed(() => {
    const limits = this.limits();
    if (!limits) return '';
    return `You have used ${limits.currentToolAssemblies} of ${limits.maximumToolAssemblies} assemblies allowed by your subscription plan.`;
  });
}
